/**
 * OrganizationDetailsView — read-only display of the signed-in organization
 * (Sep-3): every org user can confirm which organization they belong to.
 * Platform administrators get the full organizations management surface
 * instead (all organizations under the umbrella).
 */
import React, { useEffect, useState } from 'react';
import { fetchWithAuth } from '../../../utils/api';
import { useAccess, Capability } from '../../../hooks/useAccess';
import V3OrganizationsManagement from './V3OrganizationsManagement';

interface MyOrganization {
  organization_id: string;
  organization_code: string;
  name: string;
  description: string;
  created_at: string;
}

const OrgDetailsCard: React.FC = () => {
  const [org, setOrg] = useState<MyOrganization | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWithAuth('/v1/auth/my-organization')
      .then(setOrg)
      .catch(() => setError('Failed to load organization details'));
  }, []);

  if (error) return <div className="p-6 text-red-700">{error}</div>;
  if (!org) return <div className="p-6 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-xl font-semibold mb-1">{org.name}</h2>
      <p className="text-sm text-muted-foreground mb-6">
        You are signed in to this organization.
      </p>
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="font-medium text-muted-foreground">Organization code</dt>
          <dd>{org.organization_code}</dd>
        </div>
        {org.description && (
          <div>
            <dt className="font-medium text-muted-foreground">Description</dt>
            <dd>{org.description}</dd>
          </div>
        )}
        <div>
          <dt className="font-medium text-muted-foreground">Created</dt>
          <dd>{new Date(org.created_at).toLocaleDateString()}</dd>
        </div>
      </dl>
    </div>
  );
};

const OrganizationDetailsView: React.FC = () => {
  const { can, isLoading } = useAccess();
  if (isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  // Platform admins manage all organizations; org users see their own details.
  return can(Capability.PlatformManage) ? <V3OrganizationsManagement /> : <OrgDetailsCard />;
};

export default OrganizationDetailsView;
